/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { AxiosResponse } from "axios";
import { merge } from "lodash";
import { MultiSearchRequestsSchema } from "typesense/lib/Typesense/MultiSearch";

import { error as logError } from "../../util/log";
import { SelectedFiltersType } from "../products/components/ProductFilter";
import {
  ActivePrice,
  ActivePrices,
  getActivePrices,
  getStockAvailabilities,
  StockAvailabilityType,
} from "../products/storage";

import { typesenseClient } from "./Client";
import {
  BrandResponse,
  CategoryResponse,
  DocumentSchema,
  SearchResponse,
  SearchResponseHit,
  TypesenseProduct,
  TypesenseProductPriceBreak,
  TypesenseProductPromotion,
} from "./types";
import { generateSearchRequests } from "./utils";

export const collection = "products";

export interface MultiSearchParams {
  accountNumber: string | undefined;
  currentPage: number;
  selectedFilters: SelectedFiltersType;
  sortBy: string;
  branchId?: string;
}

export async function multiSearchWithActivePrices({
  accountNumber,
  currentPage,
  selectedFilters,
  sortBy,
  branchId,
}: MultiSearchParams): Promise<{ searchResults: SearchResponse<TypesenseProduct & ActivePrice> }> {
  const { searchResults } = await multiSearch(currentPage, selectedFilters, sortBy);
  const ids = searchResults.hits?.map((e) => parseInt(e.document.id.toString(), 10)) ?? [];

  const promises = [];
  let productIdsWithStock: number[] = [];

  if (searchResults.hits && ids.length > 0) {
    promises.push(getActivePrices(accountNumber, ids, branchId));
    productIdsWithStock =
      searchResults.hits
        ?.filter((hit) => !hit.document.has_variants)
        .map((hit) => parseInt(hit.document.id.toString(), 10)) ?? [];

    if (productIdsWithStock.length > 0) {
      promises.push(getStockAvailabilities({ productIds: productIdsWithStock }));
    }

    const results = await Promise.all(promises);
    const activePrices = results[0] as AxiosResponse<ActivePrices> | undefined;
    const stockAvailabilities = results[1] as StockAvailabilityType[] | undefined;

    if (activePrices?.status.toString().startsWith("2")) {
      const data = activePrices.data;

      searchResults.hits = searchResults.hits.map((e) => {
        const match = data.prices.find((p) => {
          return p.productId.toString() === e.document.id.toString();
        });

        let stockAvailabilitiesMatch: StockAvailabilityType[] | undefined = undefined;
        if (stockAvailabilities) {
          stockAvailabilitiesMatch = stockAvailabilities?.filter((s) => {
            return s.productId.toString() === e.document.id.toString();
          });
        }

        let doc: TypesenseProduct & ActivePrice;
        if (match) {
          doc = merge(e.document, match, { stockAvailabilities }) as TypesenseProduct & ActivePrice;
        } else {
          doc = merge(
            e.document,
            {
              discountedPriceIncGst: 0,
              discountedPriceExcGst: 0,
              basePriceIncGst: 0,
              basePriceExcGst: 0,
            },
            { stockAvailabilities: stockAvailabilitiesMatch }
          ) as TypesenseProduct & ActivePrice;
        }

        return {
          ...e,

          document: doc!,
        } as SearchResponseHit<TypesenseProduct & ActivePrice>;
      });
    }
  }

  return { searchResults } as { searchResults: SearchResponse<TypesenseProduct & ActivePrice> };
}

export async function multiSearch(
  currentPage: number,
  selectedFilters: SelectedFiltersType,
  sortBy: string
): Promise<{ searchResults: SearchResponse<TypesenseProduct & ActivePrice> }> {
  const searchRequests = generateSearchRequests(currentPage, selectedFilters, sortBy);

  const hits = await typesenseClient.multiSearch.perform<(TypesenseProduct & ActivePrice)[]>(
    searchRequests,
    {}
  );

  const result = hits.results[0];

  // A multiSearch returns HTTP 200 even when an individual search fails (e.g. a
  // filter referencing a field that isn't in the schema yet). Typesense puts the
  // error in the result instead of throwing, so detect it and degrade to empty
  // results — callers render the empty state rather than crashing on undefined hits.
  if (result && "error" in result) {
    logError("Typesense multiSearch returned an error:", result);
    return {
      searchResults: { found: 0, hits: [], page: currentPage } as unknown as SearchResponse<
        TypesenseProduct & ActivePrice
      >,
    };
  }

  return {
    searchResults: result,
  };
}

export async function multiSearchQuery<T extends DocumentSchema>(
  searchParams: MultiSearchRequestsSchema
): Promise<{ searchResults: SearchResponse<T> }> {
  const hits = await typesenseClient.multiSearch.perform<T[]>(searchParams, {});
  return {
    searchResults: hits.results[0],
  };
}

type Empty = Record<string, never>;

export async function getCategories() {
  const hits = await typesenseClient.multiSearch.perform<Empty[]>(
    {
      searches: [
        {
          collection: collection,
          per_page: 0,
          facet_by: "categories",
          max_facet_values: 1000,
          q: "*",
          query_by: "name",
          sort_by: "_text_match:desc",
          exclude_fields: "embedding",
        },
      ],
    },
    {},
    {
      cacheSearchResultsForSeconds: 3600,
    }
  );

  const categories =
    hits.results[0].facet_counts?.[0].counts.map(
      (e) =>
        ({
          count: e.count,
          value: e.value,
        }) as CategoryResponse
    ) ?? [];
  return {
    categories: [...categories].sort((a, b) => (a.value > b.value ? 1 : -1)),
  };
}

export async function getBrands() {
  const hits = await typesenseClient.multiSearch.perform<Empty[]>(
    {
      searches: [
        {
          collection: collection,
          per_page: 0,
          facet_by: "brand",
          max_facet_values: 1000,
          q: "*",
          query_by: "name",
          sort_by: "_text_match:desc",
          exclude_fields: "embedding",
        },
      ],
    },
    {},
    {
      cacheSearchResultsForSeconds: 3600,
    }
  );

  const brands =
    hits.results[0].facet_counts?.[0].counts.map(
      (e) =>
        ({
          count: e.count,
          value: e.value,
        }) as BrandResponse
    ) ?? [];
  return {
    brands: brands,
  };
}

export async function autoSearch(searchInput: string) {
  if (!searchInput) {
    return {} as SearchResponse<TypesenseProduct>;
  }

  const hits = await typesenseClient.multiSearch.perform<TypesenseProduct[]>({
    searches: [
      {
        collection: collection,
        per_page: 4,
        page: 1,
        q: searchInput,
        query_by: "name,item_id,online_description,brand,embedding,l1_category,l2_category",
        exclude_fields: "embedding",
      },
    ],
  });

  return hits.results[0];
}

export async function getProductByMasterProductId(
  masterId: number
): Promise<TypesenseProduct | undefined> {
  const hits = await typesenseClient.multiSearch.perform<TypesenseProduct[]>({
    searches: [
      {
        collection: collection,
        filter_by: `id:=${masterId}`,
        q: "*",
        query_by: "name",
        exclude_fields: "embedding",
      },
    ],
  });

  let product: TypesenseProduct | undefined;
  if (hits.results[0].hits && hits.results[0].hits?.length > 0) {
    product = hits.results[0].hits[0].document;
  }

  return product;
}

/**
 * Fetches promotions for the given master product ids from Typesense, keyed by id.
 *
 * Workaround for the cart: the cart's backend sync response does not carry the
 * promotion/trader value, so we re-query Typesense (the source of truth for
 * promotions) for the items currently in the cart. Returns a single request
 * filtering on all ids at once.
 */
export async function getPromotionsByMasterProductIds(
  masterProductIds: number[]
): Promise<Record<number, TypesenseProductPromotion[] | undefined>> {
  if (masterProductIds.length === 0) {
    return {};
  }

  const hits = await typesenseClient.multiSearch.perform<TypesenseProduct[]>({
    searches: [
      {
        collection,
        filter_by: `id:[${masterProductIds.join(",")}]`,
        q: "*",
        query_by: "name",
        per_page: 250,
        include_fields: "id,promotions",
      },
    ],
  });

  const promotionsByMasterId: Record<number, TypesenseProductPromotion[] | undefined> = {};
  hits.results[0].hits?.forEach((hit) => {
    promotionsByMasterId[hit.document.id] = hit.document.promotions;
  });

  return promotionsByMasterId;
}

export interface TypesenseProductDetails {
  lifecycle?: string;
  stock?: string;
  promotions?: TypesenseProductPromotion[];
  priceBreaks?: TypesenseProductPriceBreak[];
}

/**
 * Fetches lifecycle/stock/promotions/priceBreaks for the given master product ids from
 * Typesense, keyed by id.
 */
export async function getProductDetailsByMasterProductIds(
  masterProductIds: number[]
): Promise<Record<number, TypesenseProductDetails | undefined>> {
  if (masterProductIds.length === 0) {
    return {};
  }

  const hits = await typesenseClient.multiSearch.perform<TypesenseProduct[]>({
    searches: [
      {
        collection,
        filter_by: `id:[${masterProductIds.join(",")}]`,
        q: "*",
        query_by: "name",
        per_page: 250,
        include_fields: "id,lifecycle,stock,promotions,priceBreaks",
      },
    ],
  });

  const detailsByMasterId: Record<number, TypesenseProductDetails | undefined> = {};
  hits.results[0].hits?.forEach((hit) => {
    detailsByMasterId[hit.document.id] = {
      lifecycle: hit.document.lifecycle,
      stock: hit.document.stock,
      promotions: hit.document.promotions,
      priceBreaks: hit.document.priceBreaks,
    };
  });

  return detailsByMasterId;
}

/**
 * Fetches the first product that matches the given itemNumber from Typesense.
 *
 * @param itemNumber - The item number to search for.
 * @returns The first matching product's details or undefined if not found.
 */
export async function getProductByItemNumber(
  itemNumber: string
): Promise<TypesenseProduct | undefined> {
  try {
    // Perform a Typesense search to find the product by itemNumber
    const hits = await typesenseClient.multiSearch.perform<TypesenseProduct[]>({
      searches: [
        {
          collection: "products",
          filter_by: `item_id:=${itemNumber}`,
          q: "*",
          query_by: "item_id",
          exclude_fields: "embedding",
        },
      ],
    });

    // Check if there are any matching hits
    if (hits.results[0]?.hits?.length && hits.results[0]?.hits?.length > 0) {
      return hits.results[0].hits[0].document;
    }

    // Return undefined if no product is found
    return undefined;
  } catch (error) {
    logError("Error fetching product by item number:", error);
    throw error;
  }
}
